import React from "react";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { prefixer } from "stylis";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  Chip,
  Tooltip,
  ThemeProvider,
  createTheme,
  useTheme,
} from "@mui/material";
import {
  X,
  Trash2,
  Plus,
  Minus,
  ShoppingCart,
  ArrowRight,
  Package,
} from "lucide-react";

const TEAL = "#09354b";
const TEAL_DARK = "#004D40";
const ORANGE = "#F97316";
const TEXT = "#1A202C";
const TEXT_SEC = "#64748B";
const BORDER = "#E2E8F0";

const ltrCache = createCache({
  key: "cart-ltr",
  stylisPlugins: [prefixer],
});

const fmt = (n) =>
  n === 0
    ? "Free"
    : new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(n);

const CartDrawer = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.resolvedLanguage;
  const isRTL = i18n.dir() === "rtl";
  const history = useHistory();
  const { items, isOpen } = useSelector((s) => s.cart);
  const currentTheme = useTheme();
  // Force LTR on the Drawer so MUI's position + Slide animation always go right→left,
  // regardless of the page's RTL theme direction.
  const ltrTheme = React.useMemo(
    () => createTheme({ ...currentTheme, direction: "ltr" }),
    [currentTheme],
  );
  const getItemKey = React.useCallback(
    (item) => item.cartLineId || item._id,
    [],
  );

  const close = () => dispatch({ type: "CART_CLOSE" });

  const remove = (id) => dispatch({ type: "CART_REMOVE_ITEM", payload: id });

  const updateCount = (item, delta) => {
    const next = Math.max(1, item.userCount + delta);
    dispatch({
      type: "CART_UPDATE_ITEM",
      payload: { ...item, userCount: next },
    });
  };

  const toggleBilling = (item) => {
    const billingType = item.billingType === "monthly" ? "yearly" : "monthly";
    dispatch({ type: "CART_UPDATE_ITEM", payload: { ...item, billingType } });
  };

  const getItemPrice = (item) => {
    const base =
      item.billingType === "yearly"
        ? item.basePricePerYear
        : item.basePricePerMonth;
    if (item.pricingModel === "flat") {
      return base || 0;
    }
    return (base || 0) * item.userCount;
  };

  const isTrialItem = (item) => Number(item.trialDays || 0) > 0;
  const getItemTotal = (item) => getItemPrice(item) + (item.setupFee || 0);
  const subtotal = items.reduce(
    (sum, i) => sum + (isTrialItem(i) ? 0 : getItemPrice(i)),
    0,
  );
  const setupTotal = items.reduce(
    (sum, i) => sum + (isTrialItem(i) ? 0 : i.setupFee || 0),
    0,
  );
  const total = subtotal + setupTotal;
  const amountDueAfterTrial = items.reduce(
    (sum, i) => sum + (isTrialItem(i) ? getItemTotal(i) : 0),
    0,
  );

  const goCheckout = () => {
    close();
    history.push(`/${currentLang}/checkout`);
  };

  return (
    <CacheProvider value={ltrCache}>
      <ThemeProvider theme={ltrTheme}>
        <Drawer
          anchor="right"
          open={isOpen}
          onClose={close}
          ModalProps={{
            sx: { direction: "ltr" },
          }}
          SlideProps={{
            direction: "left",
          }}
          PaperProps={{
            sx: {
              width: { xs: "100vw", sm: 420 },
              display: "flex",
              flexDirection: "column",
              bgcolor: "#FAFAFA",
              direction: "ltr",
              right: 0,
              left: "auto !important",
            },
          }}
        >
        {/* Restore RTL direction for Arabic content inside the drawer */}
        <Box
          dir={isRTL ? "rtl" : "ltr"}
          sx={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2.5,
              bgcolor: "#ffffff",
              borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <ShoppingCart size={22} color={TEAL} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: TEXT }}>
                {t("shopping_cart") || "سلة التسوق"}
              </Typography>
              {items.length > 0 && (
                <Chip
                  label={items.length}
                  size="small"
                  sx={{
                    bgcolor: TEAL,
                    color: "#fff",
                    fontWeight: 700,
                    height: 22,
                    minWidth: 22,
                  }}
                />
              )}
            </Box>
            <IconButton onClick={close} size="small">
              <X size={20} />
            </IconButton>
          </Box>

          {/* Empty state */}
          {items.length === 0 && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
                textAlign: "center",
              }}
            >
              <Package size={56} color={BORDER} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: TEXT, mt: 2, mb: 1 }}
              >
                {t("cart_empty") || "السلة فارغة"}
              </Typography>
              <Typography variant="body2" sx={{ color: TEXT_SEC, mb: 3 }}>
                {t("cart_empty_hint") || "أضف باقة من صفحة الأسعار للبدء"}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  close();
                  history.push(`/${currentLang}/prices`);
                }}
                sx={{
                  borderColor: TEAL,
                  color: TEAL,
                  borderRadius: "50px",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {t("browse_packages") || "تصفح الباقات"}
              </Button>
            </Box>
          )}

          {/* Items list */}
          {items.length > 0 && (
            <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
              {items.map((item) => {
                const price = getItemPrice(item);
                return (
                  <Box
                    key={getItemKey(item)}
                    sx={{
                      bgcolor: "#ffffff",
                      border: `1px solid ${BORDER}`,
                      borderRadius: 3,
                      p: 2.5,
                      mb: 2,
                    }}
                  >
                    {/* Package name + remove */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1.5,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, color: TEXT }}
                        >
                          {item.packageName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: TEXT_SEC }}>
                          {item.description}
                        </Typography>
                      </Box>
                      <Tooltip title={t("remove") || "حذف"}>
                        <IconButton
                          size="small"
                          onClick={() => remove(getItemKey(item))}
                          sx={{ color: "#EF4444", mt: -0.5 }}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Billing toggle */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: TEXT_SEC }}>
                        {t("billing") || "الدفع"}:
                      </Typography>
                      <Box
                        sx={{
                          display: "inline-flex",
                          border: `1px solid ${BORDER}`,
                          borderRadius: "50px",
                          overflow: "hidden",
                        }}
                      >
                        {["monthly", "yearly"].map((type) => (
                          <Box
                            key={type}
                            onClick={() =>
                              item.billingType !== type && toggleBilling(item)
                            }
                            sx={{
                              px: 1.5,
                              py: 0.3,
                              cursor: "pointer",
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              bgcolor:
                                item.billingType === type
                                  ? TEAL
                                  : "transparent",
                              color:
                                item.billingType === type ? "#fff" : TEXT_SEC,
                              transition: "all 0.2s",
                              userSelect: "none",
                            }}
                          >
                            {type === "monthly"
                              ? t("monthly") || "شهري"
                              : t("yearly") || "سنوي"}
                          </Box>
                        ))}
                      </Box>
                      {item.billingType === "yearly" && (
                        <Chip
                          label={t("save_20") || "وفر 20%"}
                          size="small"
                          sx={{
                            bgcolor: `${ORANGE}18`,
                            color: ORANGE,
                            fontWeight: 700,
                            fontSize: "0.65rem",
                            height: 18,
                          }}
                        />
                      )}
                      {item.trialDays > 0 && (
                        <Chip
                          label={`${item.trialDays} ${t("days_free_trial") || "days free trial"}`}
                          size="small"
                          sx={{
                            bgcolor: "#ECFDF5",
                            color: "#047857",
                            fontWeight: 700,
                            fontSize: "0.65rem",
                            height: 18,
                          }}
                        />
                      )}
                    </Box>

                    {/* User count */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="caption" sx={{ color: TEXT_SEC }}>
                          {t("users") || "مستخدمين"}:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            border: `1px solid ${BORDER}`,
                            borderRadius: "8px",
                            overflow: "hidden",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => updateCount(item, -1)}
                            disabled={item.userCount <= 1}
                            sx={{ borderRadius: 0, p: 0.5 }}
                          >
                            <Minus size={14} />
                          </IconButton>
                          <Typography
                            variant="body2"
                            sx={{
                              px: 1.5,
                              fontWeight: 700,
                              minWidth: 24,
                              textAlign: "center",
                            }}
                          >
                            {item.userCount}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => updateCount(item, 1)}
                            sx={{ borderRadius: 0, p: 0.5 }}
                          >
                            <Plus size={14} />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Line price */}
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, color: TEAL }}
                        >
                          {fmt(price)}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: TEXT_SEC, fontWeight: 400 }}
                          >
                            /
                            {item.billingType === "yearly"
                              ? t("yr") || "سنة"
                              : t("mo") || "شهر"}
                          </Typography>
                        </Typography>
                        {item.setupFee > 0 && (
                          <Typography
                            variant="caption"
                            sx={{ color: TEXT_SEC, display: "block" }}
                          >
                            + {fmt(item.setupFee)} {t("setup") || "تركيب"}
                          </Typography>
                        )}
                        {item.trialDays > 0 && (
                          <Typography
                            variant="caption"
                            sx={{ color: "#047857", display: "block", fontWeight: 700 }}
                          >
                            {t("due_today") || "Due today"}: {fmt(0)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Footer totals + checkout */}
          {items.length > 0 && (
            <Box
              sx={{
                bgcolor: "#ffffff",
                borderTop: `1px solid ${BORDER}`,
                p: 3,
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="body2" sx={{ color: TEXT_SEC }}>
                  {t("subtotal") || "المجموع الفرعي"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: TEXT }}
                >
                  {fmt(subtotal)}
                </Typography>
              </Box>
              {setupTotal > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: TEXT_SEC }}>
                    {t("setup_fees") || "رسوم التركيب"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: TEXT }}
                  >
                    {fmt(setupTotal)}
                  </Typography>
                </Box>
              )}
              {amountDueAfterTrial > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: TEXT_SEC }}>
                    {t("amount_after_trial") || "Amount after trial"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: TEXT }}
                  >
                    {fmt(amountDueAfterTrial)}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ my: 1.5 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 2.5,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: TEXT }}
                >
                  {t("due_today") || "Due today"}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, color: TEAL }}
                >
                  {fmt(total)}
                </Typography>
              </Box>
              <Button
                fullWidth
                variant="contained"
                size="large"
                endIcon={<ArrowRight size={18} />}
                onClick={goCheckout}
                sx={{
                  bgcolor: TEAL,
                  borderRadius: "50px",
                  textTransform: "none",
                  fontWeight: 700,
                  py: 1.5,
                  fontSize: "1rem",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: TEAL_DARK,
                    boxShadow: `0 8px 20px ${TEAL}44`,
                  },
                }}
              >
                {t("proceed_to_checkout") || "المتابعة للدفع"}
              </Button>
              <Button
                fullWidth
                variant="text"
                size="small"
                onClick={close}
                sx={{ mt: 1, color: TEXT_SEC, textTransform: "none" }}
              >
                {t("continue_shopping") || "مواصلة التسوق"}
              </Button>
            </Box>
          )}
        </Box>
        {/* end RTL content wrapper */}
        </Drawer>
      </ThemeProvider>
    </CacheProvider>
  );
};

export default CartDrawer;
